import { useState, type ReactNode } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import {
  Box,
  Card,
  CardContent,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { dayjs } from "@thalamos/common";
import { tk, type IncidentWorkItemWithContext } from "@thalamos/vision-common";

import { TypographyI18N } from "../../components/TypographyI18N/TypographyI18N.js";

const PATIENT_ACCENT_COLOUR = "#9DC9FB";

interface PatientDetailsCardProps {
  incident: IncidentWorkItemWithContext;
  defaultExpanded?: boolean;
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        py: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", fontWeight: 500, minWidth: 120 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, textAlign: "right", wordBreak: "break-word" }}
      >
        {value ?? "—"}
      </Typography>
    </Box>
  );
}

/**
 * Extracts patient/person details from the createIncident form data.
 */
function getPersonDetails(
  incident: IncidentWorkItemWithContext,
  unknownLabel: string,
): {
  name: string;
  dateOfBirth: string;
  address: string;
} {
  const createForm = incident.forms.find(
    (f) => f.formType === "createIncident",
  );
  if (!createForm)
    return {
      name: unknownLabel,
      dateOfBirth: unknownLabel,
      address: unknownLabel,
    };

  // Person details are stored in form section data
  const allData = createForm.sections.reduce<Record<string, unknown>>(
    (acc, section) => ({ ...acc, ...section.data }),
    {},
  );

  const forename = extractFieldValue(allData, "personForename");
  const surname = extractFieldValue(allData, "personSurname");
  const dob = extractFieldValue(allData, "dateOfBirth");
  const address = extractFieldValue(allData, "personAddress");

  const name = [forename, surname].filter(Boolean).join(" ") || unknownLabel;
  const formattedDob = dob
    ? dayjs.utc(dob).tz("Europe/London").format("DD/MM/YYYY")
    : unknownLabel;

  return {
    name,
    dateOfBirth: formattedDob,
    address: address || unknownLabel,
  };
}

function extractFieldValue(data: Record<string, unknown>, key: string): string {
  const field = data[key];
  if (!field) return "";
  if (typeof field === "string") return field;
  if (typeof field === "object" && field !== null && "value" in field) {
    const val = (field as { value: unknown }).value;
    if (val === null || val === undefined) return "";
    if (typeof val === "string") return val;
    if (typeof val === "number" || typeof val === "boolean") return String(val);
    return "";
  }
  return "";
}

export function PatientDetailsCard({
  incident,
  defaultExpanded = true,
}: PatientDetailsCardProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const unknownLabel = t(tk("incidentSummary.unknown"));
  const person = getPersonDetails(incident, unknownLabel);

  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        borderLeft: `4px solid ${PATIENT_ACCENT_COLOUR}`,
        overflow: "visible",
      }}
      data-testid="patient-details-card"
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: 2 } }}>
        {/* Header row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
          onClick={() => {
            setExpanded((v) => !v);
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonOutlineIcon
              fontSize="small"
              sx={{
                color: PATIENT_ACCENT_COLOUR,
              }}
            />
            <TypographyI18N
              variant="subtitle1"
              sx={{ fontWeight: 700 }}
              translationKey={tk("incidentSummary.patientDetails")}
            />
          </Box>
          <IconButton
            size="small"
            aria-label="toggle patient details"
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: theme.transitions.create("transform", {
                duration: theme.transitions.duration.short,
              }),
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        <Collapse in={expanded} timeout="auto">
          <Divider sx={{ my: 1.5 }} />
          <Stack>
            <DetailRow
              label={t(tk("incidentSummary.personName"))}
              value={person.name}
            />
            <DetailRow
              label={t(tk("incidentSummary.dateOfBirth"))}
              value={person.dateOfBirth}
            />
            <DetailRow
              label={t(tk("incidentSummary.address"))}
              value={person.address}
            />
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  );
}
